import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Clock, House, LayoutGrid, Search, Star } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';
import { placementPath, type Board } from '@kaba/shared';
import { useBoards } from '../../lib/board';
import { recentBoardIds, useToggleFavorite } from '../../lib/boardNav';
import { navigate } from '../../lib/router';
import { cx, Sheet } from '../ui/ui';

/**
 * Board wechseln: Suche, Favoriten (am Konto), zuletzt geöffnet (dieses Gerät), alle Boards.
 * ↑/↓ wandern durch die Treffer, Enter öffnet, der Stern merkt ein Board als Favorit.
 * Oben steht zusätzlich „Zur Übersicht“ – als Web-App auf dem iPad liegen die Fenstersymbole
 * über dem Zurück-Pfeil im Kopf, hier bleibt der Weg zur Startseite immer erreichbar.
 */
export function BoardSwitcher({ open, onClose, currentId }: { open: boolean; onClose: () => void; currentId: string }) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Board wechseln" side="center">
      {open && <SwitcherBody onClose={onClose} currentId={currentId} />}
    </Sheet>
  );
}

function SwitcherBody({ onClose, currentId }: { onClose: () => void; currentId: string }) {
  const { data: boards } = useBoards();
  const [q, setQ] = useState('');
  const list = useRef<HTMLDivElement>(null);
  const fav = useToggleFavorite();

  const sections = useMemo(() => {
    const all = boards ?? [];
    const term = q.trim().toLowerCase();
    if (term) {
      // Auch Bereichs- und Ordnernamen finden (nur sichtbare Pfade)
      const hits = all.filter((b) => `${b.title} ${b.placement?.kind === 'visible' ? placementPath(b.placement) : ''}`.toLowerCase().includes(term));
      // Treffer am Wortanfang zuerst
      hits.sort((a, b) => Number(!a.title.toLowerCase().startsWith(term)) - Number(!b.title.toLowerCase().startsWith(term)));
      return [{ title: `Treffer (${hits.length})`, icon: Search, boards: hits }];
    }
    const byId = new Map(all.map((b) => [b.id, b]));
    const favs = all.filter((b) => b.favorite).sort((a, b) => a.title.localeCompare(b.title, 'de'));
    const recent = recentBoardIds()
      .map((id) => byId.get(id))
      .filter((b): b is Board => !!b && !b.favorite)
      .slice(0, 5);
    const shown = new Set([...favs, ...recent].map((b) => b.id));
    const rest = all.filter((b) => !shown.has(b.id)).sort((a, b) => a.title.localeCompare(b.title, 'de'));
    return [
      { title: 'Favoriten', icon: Star, boards: favs },
      { title: 'Zuletzt geöffnet', icon: Clock, boards: recent },
      { title: favs.length || recent.length ? 'Weitere Boards' : 'Alle Boards', icon: LayoutGrid, boards: rest },
    ].filter((s) => s.boards.length);
  }, [boards, q]);

  const go = (id: string) => {
    onClose();
    if (id !== currentId) navigate(`/b/${id}`);
  };

  // Pfeiltasten: zwischen Suchfeld und den Board-Einträgen wandern
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(list.current?.querySelectorAll<HTMLButtonElement>('[data-board-option]') ?? [])];
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? (i < 0 ? 0 : Math.min(items.length - 1, i + 1)) : i <= 0 ? -1 : i - 1;
    if (next < 0) (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus();
    else items[next]!.focus();
  };

  const first = sections[0]?.boards[0];
  return (
    <div className="flex flex-col" onKeyDown={onKey}>
      <div className="relative border-b border-[var(--line)] p-3">
        <Search size={16} className="absolute top-1/2 left-5.5 -translate-y-1/2 text-[var(--ink-soft)]" aria-hidden />
        <input
          autoFocus
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && first) {
              e.preventDefault();
              go(first.id);
            }
          }}
          placeholder="Board suchen …"
          aria-label="Board suchen"
          className="min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] pr-2 pl-8 text-base sm:min-h-10 sm:text-sm"
        />
      </div>
      <div ref={list} className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-2 sm:max-h-[55vh]">
        {!q.trim() && (
          <button
            type="button"
            data-board-option
            onClick={() => {
              onClose();
              navigate('/');
            }}
            className="flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-2.5 text-left text-sm font-medium outline-none hover:bg-black/5 focus-visible:bg-black/5 focus-visible:ring-2 focus-visible:ring-accent dark:hover:bg-white/10 dark:focus-visible:bg-white/10"
          >
            <House size={17} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
            Zur Übersicht
          </button>
        )}
        {!sections.length && <p className="p-3 text-sm text-[var(--ink-soft)]">{boards ? 'Kein Board gefunden.' : 'Lade …'}</p>}
        {sections.map((s) => (
          <section key={s.title} aria-label={s.title}>
            <h3 className="flex items-center gap-1.5 px-2 pb-1 text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">
              <s.icon size={13} aria-hidden /> {s.title}
            </h3>
            <ul>
              {s.boards.map((b) => (
                <li key={b.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    data-board-option
                    onClick={() => go(b.id)}
                    aria-current={b.id === currentId ? 'page' : undefined}
                    className={cx(
                      'flex min-h-11 min-w-0 flex-1 items-center rounded-lg px-2.5 text-left text-sm outline-none hover:bg-black/5 focus-visible:bg-black/5 focus-visible:ring-2 focus-visible:ring-accent dark:hover:bg-white/10 dark:focus-visible:bg-white/10',
                      b.id === currentId && 'font-semibold',
                    )}
                  >
                    {(b.brand?.mark || b.brand?.logo) && (
                      <BrandLogo
                        logo={{ main: (b.brand.mark ?? b.brand.logo)!, dark: null, mark: null, ratio: b.brand.mark ? 1 : b.brand.ratio, markRatio: null }}
                        name=""
                        size="tile"
                        className="mr-2 shrink-0"
                      />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col py-1">
                      <span className="truncate">{b.title}</span>
                      {b.placement && b.placement.kind !== 'none' && (
                        <span className="truncate text-xs font-normal text-[var(--ink-soft)]">{placementPath(b.placement)}</span>
                      )}
                    </span>
                    {b.id === currentId && <span className="ml-2 shrink-0 text-xs font-normal text-[var(--ink-soft)]">geöffnet</span>}
                  </button>
                  <FavoriteButton board={b} onToggle={(favorite) => fav.mutate({ id: b.id, favorite })} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

export function FavoriteButton({ board, onToggle, className }: { board: Pick<Board, 'title' | 'favorite'>; onToggle: (favorite: boolean) => void; className?: string }) {
  const on = !!board.favorite;
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? `„${board.title}“ aus den Favoriten nehmen` : `„${board.title}“ als Favorit merken`}
      title={on ? 'Favorit (gilt auf allen deinen Geräten)' : 'Als Favorit merken'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(!on);
      }}
      className={cx(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-lg hover:bg-black/5 sm:size-9 dark:hover:bg-white/10',
        on ? 'text-amber-500' : 'text-[var(--ink-soft)]',
        className,
      )}
    >
      <Star size={17} className={on ? 'fill-amber-400' : ''} aria-hidden />
    </button>
  );
}
